// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import type {
  GetBrandDto,
  MachineAttachmentDto,
  MachineDto,
  StockMachineAttachmentDto,
  StockMachineDto
} from '@api/machines'
import { StockTypes } from '@common/utils'
import { memo, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MACHINE_TYPE,
  type MachineTypeValues
} from '../select-machine-dialog/SelectMachineDialog'
import { StyledGrid } from '../styled-components/styles'

type SelectMachineStepType = {
  getStockAttachmentsByBrandId: (
    brandId: number,
    tonnage?: number
  ) => Promise<StockMachineAttachmentDto[] | undefined>
  getStockMachinesByBrandId: (
    brandId: number
  ) => Promise<StockMachineDto[] | undefined>
  inStockChecked?: boolean
  onRowClick: (machine: MachineAttachmentDto | MachineDto) => void
  selectedBrand?: GetBrandDto | null
  tonnage?: number
  translationPrefix?: string
  type: MachineTypeValues
}

const arePropsEqual = (
  oldProps: SelectMachineStepType,
  newProps: SelectMachineStepType
) => {
  if (oldProps.inStockChecked !== newProps.inStockChecked) {
    return false
  }

  if (oldProps.selectedBrand?.id === newProps.selectedBrand?.id) {
    return true
  }

  if (oldProps.type === newProps.type) {
    return true
  }

  return false
}

export const SelectMachineStep = memo(
  ({
    getStockAttachmentsByBrandId,
    getStockMachinesByBrandId,
    inStockChecked = false,
    onRowClick,
    selectedBrand,
    tonnage,
    translationPrefix = '',
    type
  }: SelectMachineStepType) => {
    const [machines, setMachines] = useState<
      StockMachineAttachmentDto[] | StockMachineDto[]
    >([])

    const { t } = useTranslation('', {
      keyPrefix: translationPrefix ?? 'common'
    })

    useEffect(() => {
      const getMachines = async () => {
        if (!selectedBrand?.id) {
          return
        }

        let machinesAux: StockMachineAttachmentDto[] | StockMachineDto[] = []

        if (type === MACHINE_TYPE.ATTACHMENT) {
          machinesAux = (await getStockAttachmentsByBrandId?.(
            selectedBrand?.id,
            tonnage
          )) as StockMachineAttachmentDto[]
        } else if (type === MACHINE_TYPE.EQUIPMENT) {
          machinesAux = (await getStockMachinesByBrandId(
            selectedBrand?.id
          )) as StockMachineDto[]
        }

        setMachines(machinesAux)
      }

      getMachines()

      return () => setMachines([])
    }, [
      selectedBrand,
      getStockAttachmentsByBrandId,
      getStockMachinesByBrandId,
      type,
      tonnage
    ])

    const getDataSource = () => {
      if (inStockChecked) {
        return machines.filter(
          machine => machine.stockType === StockTypes.IN_STOCK
        )
      }

      return machines
    }

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    const handleOptionChanged = (e: any) => {
      const { element } = e
      element?.parentNode?.scrollTo({
        top: 0
      })
    }

    return (
      <StyledGrid
        columnsProps={[
          {
            caption: t('model'),
            dataField: 'modelName'
          },
          {
            alignment: 'left',
            caption: t('category'),
            dataField: 'category'
          },
          {
            alignment: 'right',
            caption: t('manufactureYear'),
            dataField: 'manufactureYear',
            dataType: 'number'
          },
          {
            alignment: 'left',
            allowFiltering: !inStockChecked,
            allowSorting: !inStockChecked,
            caption: t('stockType'),
            dataField: 'stockType'
          }
        ]}
        headerFilterProps={{
          visible: true
        }}
        onCellPrepared={e =>
          e.rowType === 'data' &&
          e.cellElement.style.setProperty('cursor', 'pointer', 'important')
        }
        onSelectionChanged={selectedRows => {
          const { selectedRowsData } = selectedRows

          const selectedRow = selectedRowsData[0]
          onRowClick(selectedRow)
        }}
        searchPanelProps={{
          highlightSearchText: true,
          visible: true
        }}
        selection={{
          mode: 'single'
        }}
        dataSource={getDataSource()}
        onContentReady={handleOptionChanged}
      />
    )
  },
  arePropsEqual
)
